import { IconButton, TextField, Tooltip } from '@mui/material'
import { useFormik } from 'formik'
import { FC } from 'react'
import * as yup from 'yup'
import { useInvalidateWorkplaceListQuery } from '../../api/hooks/useInvalidateWorkplaceListQuery'
import { useAssistantAdministrationStore } from '../../store/storeAssistantAdministration'
import { useWrapperStore } from '../../store/storeWrapper'

const validationSchema = yup.object({
    title: yup
        .string()
        .matches(
            /[a-zA-Zа-яА-Я0-9,.;:?!№()-]/,
            'Название проекта должно содержать только буквы (русские и латинские), цифры и спецсимволы: ,.;:?!№()-',
        )
        .required('Название обязательное'),
})

import { Button, Switch } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import * as React from 'react'
import CircleInformationIcon from '../../assets/icons/CircleInformation.svg'
import { ModalDialogWrapper } from '../../components/ModalDialogWrapper'
import { ProtectedIcon } from '../../components/ProtectedIcon/ProtectedIcon'
import { ProjectProtectedOffDialog } from '../ProjectProtectedOffDialog/ProjectProtectedOffDialog'

type Props = {
    open: boolean
    handleClose: VoidFunction
    id: string
}

export const ProjectRenameDialog: FC<Props> = ({ workplaceDetail, handleClose, open }) => {
    const [isProtected, setIsProtected] = React.useState(workplaceDetail.is_protected)
    const [openProjectProtectedOffDialog, setOpenProjectProtectedOffDialog] =
        React.useState<boolean>(false)
    const handelClickIsProtected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        isProtected ? setOpenProjectProtectedOffDialog(true) : setIsProtected(true)
    }

    const handleCloseProjectProtectedOffDialog = () => {
        setOpenProjectProtectedOffDialog(false)
    }
    const handleCloseClick = () => {
        resetForm()
        setIsProtected(workplaceDetail.is_protected)
        handleClose()
    }

    const handleSubmitProjectProtectedOffDialog = () => {
        setIsProtected(false)
        setOpenProjectProtectedOffDialog(false)
    }

    const { updateWorkplace, isLoadingUpdateWorkplace } = useAssistantAdministrationStore(
        (state) => state,
    )

    const invalidateWorkplaceListQuery = useInvalidateWorkplaceListQuery()

    const { updateProjectDetail, fetchProjectList, setIsProtectedCurrentProject } = useWrapperStore(
        (state) => state,
    )
    const {
        values,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        dirty,
        isValid,
        errors,
        resetForm,
    } = useFormik({
        initialValues: {
            title: workplaceDetail.title,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const { title } = values
            await updateWorkplace(
                { workplaceId: workplaceDetail.id },
                { title, is_protected: isProtected },
            )
            updateProjectDetail({ title, is_protected: isProtected })
            setIsProtectedCurrentProject(isProtected)
            fetchProjectList()
            invalidateWorkplaceListQuery()
            handleClose()
        },
    })

    return (
        <ModalDialogWrapper
            open={open}
            handleClose={handleCloseClick}
            colorSubmitButton="primary"
            title="Редактирование проекта">
            <Typography variant="body2" mb={1}></Typography>

            <form onSubmit={handleSubmit}>
                <TextField
                    required
                    sx={{
                        marginBottom: '16px',
                        marginTop: 0,
                        width: '100%',
                    }}
                    margin="normal"
                    fullWidth
                    id="title"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                />
                <Typography variant="body2" sx={{ color: '#5A5C5E' }}>
                    Проект — это общее пространство, где можно создавать много разных запросов.
                    Здесь ты настраиваешь инструкции, загружаешь файлы, а ИИ учитывает весь этот
                    контекст во всех запросах внутри проекта
                </Typography>

                {workplaceDetail.is_protected && (
                    <Stack
                        my={2}
                        sx={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                        direction="row">
                        <Stack direction="row" gap={1} sx={{ alignItems: 'center' }}>
                            <ProtectedIcon />
                            <Typography
                                variant="caption1"
                                component="p"
                                sx={{
                                    color: '#181B25',
                                }}>
                                Защищенный проект
                            </Typography>
                            <Tooltip
                                enterTouchDelay={0}
                                title="«Защищённый проект» — все данные хранятся только на серверах ARHITEX в РФ, полностью принадлежащих и контролируемых компанией. При работе используются только защищённые нейросети в агрегаторе нейросетей">
                                <IconButton sx={{ p: 0, width: '20px', height: '20px' }}>
                                    <CircleInformationIcon color="#909295" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <Switch
                            onChange={handelClickIsProtected}
                            checked={isProtected}
                            size="small"
                        />
                    </Stack>
                )}

                <Stack sx={{ padding: ' 8px 0 8px 0' }} direction="row" gap={1}>
                    <Button fullWidth variant="outlined" onClick={handleCloseClick}>
                        Отменить
                    </Button>
                    <Button disabled={!isValid} type="submit" fullWidth variant="contained">
                        Сохранить
                    </Button>
                </Stack>
            </form>
            <ProjectProtectedOffDialog
                open={openProjectProtectedOffDialog}
                handleClose={handleCloseProjectProtectedOffDialog}
                handleSubmit={handleSubmitProjectProtectedOffDialog}
            />
        </ModalDialogWrapper>
    )
}
